import React from "react";
import { MenuItem } from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { WireGuardTunnel } from "../troubleshootingInterfaces";
import {
  cidrContains,
  tunnelRoutedRanges,
} from "../TroubleshootingUtils/siteTunnel";
import { isIpLiteral } from "../TroubleshootingUtils/probeTargets";

/** Tunnels whose subnets contain `host` (an IP literal), in the given list. */
export const tunnelsRouting = (
  tunnels: WireGuardTunnel[],
  host: string
): WireGuardTunnel[] => {
  const text = host.trim();
  if (!text || !isIpLiteral(text)) return [];
  return tunnels.filter((tunnel) =>
    tunnelRoutedRanges(tunnel).some((cidr) => cidrContains(cidr, text))
  );
};

/**
 * WireGuard entries for a Test from menu (ENG-2057). Each tunnel says what
 * it has to do with this check, "used by this site" and "routes <host>",
 * and those come first, but nothing is selected for the user: a subnet
 * match is a hint, not the path (a WireGuard subnet can overlap an IPsec
 * route, ENG-2050). Returned as an array because MUI Select needs its
 * MenuItems as direct children.
 */
export const tunnelMenuItems = (
  tunnels: WireGuardTunnel[],
  host: string,
  siteTunnelIds: Set<number> = new Set()
): React.ReactElement[] => {
  const routing = new Set(tunnelsRouting(tunnels, host).map((t) => t.id));
  const text = host.trim();
  const tagsFor = (tunnel: WireGuardTunnel) => {
    const tags: string[] = [];
    if (siteTunnelIds.has(tunnel.id)) tags.push("used by this site");
    if (routing.has(tunnel.id)) tags.push(`routes ${text}`);
    return tags;
  };
  return [...tunnels]
    .map((tunnel, index) => ({ tunnel, index, tags: tagsFor(tunnel) }))
    .sort((a, b) =>
      a.tags.length === b.tags.length
        ? a.index - b.index
        : b.tags.length - a.tags.length
    )
    .map(({ tunnel, tags }) => (
      <MenuItem key={tunnel.id} value={`wg:${tunnel.id}`} sx={MULISH}>
        WireGuard: {tunnel.name}
        {tags.length ? ` · ${tags.join(" · ")}` : ""}
      </MenuItem>
    ));
};
